import React, { useEffect, useState } from 'react';
import { useTranslations } from 'hooks/useTranslations';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useCase } from '../../../hooks';
import { Elabels, getBreadcrumbsBasic } from 'pages/CaseManagementPage/CasesPage/helpers';
import { useQuery } from 'hooks/useQuery';
import { isPathEqualToRouteName } from 'utils/route';
import { ERouteLinks } from 'models/route';
import Breadcrumbs from 'components/atoms/Breadcrumbs';
import { Box, Grid, Link, Stack, Typography } from '@mui/material';
import { StyledPageTitle } from 'theme/styles';
import { Loader } from 'components/atoms/Loader';
import { StyledDetail, StyledDivider } from 'pages/CaseManagementPage/CasesPage/styles';
import { getHtmlText } from 'components/organisms/HtmlSlate';
import { formatDate } from 'utils/date';
import { downloadUrlAsFile } from 'utils/files';
import Button from 'components/atoms/Button';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import { EPermission } from 'models/permissions';
import { FormModal } from 'components/molecules/Modal';
import { useAlert } from 'models/alertContext';
import { Alert } from 'components/atoms/Alert';
import { ActionTypes } from 'state/actions/alert';
import { useAuth } from 'models/authContext';
import { CaseStatus, FINAL_CASE_STATUSES } from 'models/cases';
import { colors } from 'theme/colors';

const IntelligenceReportDetailsPage = () => {
  const { clearAlert, samePageAlert, nextPageAlert } = useAlert();
  const { t } = useTranslations();
  const navigate = useNavigate();
  const { getCaseData, caseData } = useCase();
  const { user, getUser } = useAuth();
  const {
    id = '',
    caseId = '',
    reportId = '',
  } = useParams<{ id: string; caseId: string; reportId: string }>();
  const query = useQuery();
  const type: string | null = query.get('type')?.trim() || null;
  const { pathname }: any = useLocation();
  const isFromLE = isPathEqualToRouteName(
    pathname,
    ERouteLinks.LegalEntityCaseIntelligenceReportPage,
  );

  useEffect(() => {
    (async () => {
      await getCaseData(caseId);
    })();
  }, [caseId]);

  useEffect(() => {
    if (user === null) {
      getUser();
    }
  }, []);

  const { longDateFormat, getProperties } = useGlobalProperty();
  const { getReportData, report, deleteReport } = useCase();
  const [isLoading, setIsLoading] = useState(true);
  const breadcrumbs = getBreadcrumbsBasic(isFromLE, type, t, false, id);
  const [deleteModal, setDeleteModal] = useState(false);
  const [isTrace, setIsTrace] = useState(false);

  useEffect(
    () => () => {
      clearAlert(ActionTypes.CLEAR_SAME_PAGE_ALERT);
      clearAlert(ActionTypes.CLEAR_NEXT_PAGE_ALERT);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    getProperties();
  }, []);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      await getReportData(reportId);
      setIsLoading(false);
    })();
  }, []);

  useEffect(() => {
    if (report) {
      setIsTrace(report.trace !== null);
    }
  }, [report]);

  const editAction = () => {
    isFromLE
      ? navigate(
          `${ERouteLinks.LegalEntityCaseIntelligenceReportEditPage.replace(':id', id)
            .replace(':caseId', caseId)
            .replace(':reportId', reportId)}?type=${type}`,
        )
      : navigate(
          ERouteLinks.CaseIntelligenceReportEditPage.replace(':caseId', caseId).replace(
            ':reportId',
            reportId,
          ),
        );
  };

  const addReportResultAction = () => {
    isFromLE
      ? navigate(
          `${ERouteLinks.LegalEntityAddIntelligenceReportResult.replace(':id', id)
            .replace(':caseId', caseId)
            .replace(':reportId', reportId)}?type=${type}`,
        )
      : navigate(
          ERouteLinks.AddIntelligenceReportResult.replace(':caseId', caseId).replace(
            ':reportId',
            reportId,
          ),
        );
  };

  const handleDeleteReport = async () => {
    if (reportId) {
      const res = await deleteReport(reportId, true);
      if (res) {
        setDeleteModal(false);
      }
    }
    navigate(
      isFromLE
        ? `${ERouteLinks.LegalEntityCasesDetailsPage.replace(':caseId', caseId).replace(':id', id)}?type=${type}&expand=cases`
        : `${ERouteLinks.CasesDetailsPage.replace(':caseId', caseId)}`,
    );
  };
  const downloadFile = (url: string, filename: string) => {
    downloadUrlAsFile(url, filename);
  };

  const isReadOnly = FINAL_CASE_STATUSES.includes(caseData?.statusUniqueId as CaseStatus);

  return (
    <>
      <Breadcrumbs
        items={[
          ...breadcrumbs,
          {
            label: t(Elabels.caseDetailsHeader),
            route: isFromLE
              ? `${ERouteLinks.LegalEntityCasesDetailsPage.replace(':caseId', caseId).replace(':id', id)}?type=${type}&expand=cases`
              : `${ERouteLinks.CasesDetailsPage.replace(':caseId', caseId)}`,
          },
          t('caseManagement.cases.intelligenceReportDetails.label'),
        ]}
      />
      <FormModal
        open={!!deleteModal}
        onClickClose={() => setDeleteModal(false)}
        onClickApprove={handleDeleteReport}
        approveButtonLabel="general.yes.label"
        cancelButtonLabel="general.no.label"
        title={t('caseManagement.cases.deleteReport.label')}
        titleLeft
        text={t('caseManagement.cases.areYouSureDeleteReport.text')}
      />
      <Grid sx={{ justifyContent: 'space-between', alignItems: 'center' }} spacing={1}>
        <Grid
          container
          size={{ xs: 12 }}
          sx={{
            justifyContent: 'space-between',
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
            alignItems: 'center',
            mb: '20px',
          }}
          spacing={1}
        >
          <Grid container sx={{ flexWrap: 'nowrap' }} spacing={1}>
            <StyledPageTitle variant="h4">
              {t('caseManagement.cases.intelligenceReportDetails.label')}
            </StyledPageTitle>
          </Grid>
          {report &&
            caseData &&
            caseData.assignedToUniqueId === user?.userId &&
            caseData.statusUniqueId === CaseStatus.IN_PROGRESS && (
              <Grid container sx={{ flexWrap: 'nowrap' }} spacing={1}>
                {!isReadOnly && report.trace === null && (
                  <Button
                    variant={EButtonVariants.contained}
                    tableButton
                    label={t(Elabels.addResults)}
                    size={EButtonSizes.small}
                    onClick={addReportResultAction}
                    id={'add_report_result-button'}
                  />
                )}
                {!isReadOnly && report.trace !== null && (
                  <Button
                    variant={EButtonVariants.contained}
                    permission={[EPermission.VERIFY_CASES]}
                    onClick={editAction}
                    label={t(Elabels.edit)}
                    size={EButtonSizes.small}
                    id="intelligence_report_details_page_edit-button"
                  />
                )}
                {!isReadOnly && !report.fromTemplate && (
                  <Button
                    variant={EButtonVariants.contained}
                    permission={[EPermission.VERIFY_CASES]}
                    onClick={() => setDeleteModal(true)}
                    label={t(Elabels.remove)}
                    size={EButtonSizes.small}
                    id="intelligence_report_details_page_remove-button"
                  />
                )}
              </Grid>
            )}
        </Grid>

        <Grid sx={{ alignItems: 'center' }}>
          {nextPageAlert && (
            <Box>
              <Alert text={nextPageAlert.text} variant={nextPageAlert.variant} />
            </Box>
          )}
        </Grid>

        <Grid sx={{ alignItems: 'center' }}>
          {samePageAlert && (
            <Box>
              <Alert text={samePageAlert.text} variant={samePageAlert.variant} />
            </Box>
          )}
        </Grid>
        {isLoading && (
          <Grid size={{ xs: 12 }}>
            <Loader isLoading />
          </Grid>
        )}
        {!isLoading && report && (
          <Grid sx={{ justifyContent: 'space-between', alignItems: 'center', gap: '20px' }}>
            <StyledDetail container size={{ xs: 12 }}>
              <Grid size={{ xs: 12 }}>
                <Stack>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t(Elabels.sourceSystem)}
                  </Typography>
                  <Typography variant="body2">{report.sourceSystem}</Typography>
                </Stack>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Stack>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t(Elabels.instructions)}
                  </Typography>
                  <Typography variant="body2">{getHtmlText(report.instructions)}</Typography>
                </Stack>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Stack>
                  <StyledDivider />
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: colors.primary600,
                      marginTop: '4px',
                      textTransform: 'uppercase',
                    }}
                  >
                    {t(Elabels.results)}
                  </Typography>
                </Stack>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Stack>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t(Elabels.trace)}
                  </Typography>
                  <Typography variant="body2">
                    {report.trace === null
                      ? '-'
                      : report.trace
                        ? t('general.yes.label')
                        : t('general.no.label')}
                  </Typography>
                </Stack>
              </Grid>
              {isTrace && (
                <>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Stack>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(Elabels.lastUpdate)}
                      </Typography>
                      <Typography variant="body2">
                        {formatDate(report.lastUpdate, longDateFormat)}
                      </Typography>
                    </Stack>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Stack>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(Elabels.lastAuthor)}
                      </Typography>
                      <Typography variant="body2">{`${report.lastAuthorName} (${report.lastAuthorDisplayUniqueId})`}</Typography>
                    </Stack>
                  </Grid>
                </>
              )}
              {isTrace && report.trace.toString() === 'true' && (
                <Grid size={{ xs: 12 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t(Elabels.attachments)}
                    </Typography>
                    {report.attachments
                      ? report.attachments?.map((attachment) => (
                          <Typography variant="body2" sx={{ width: 'fit-content' }}>
                            <Link
                              onClick={() =>
                                downloadFile(attachment.preassignedUrl, attachment.filename)
                              }
                              underline="none"
                              rel="noopener noreferrer"
                              style={{ display: 'flex', cursor: 'pointer', gap: '8px' }}
                            >
                              {`${attachment.filename}.${attachment.extension.toLowerCase()}`}
                            </Link>
                          </Typography>
                        ))
                      : '-'}
                  </Stack>
                </Grid>
              )}
              {isTrace && report.trace.toString() === 'true' && (
                <>
                  <Grid size={{ xs: 12 }}>
                    <Stack>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t(Elabels.conclusions)}
                      </Typography>
                      <Typography variant="body2">
                        {getHtmlText(report.conclusions)}
                      </Typography>
                    </Stack>
                  </Grid>
                </>
              )}
            </StyledDetail>
          </Grid>
        )}
      </Grid>
    </>
  );
};

export default IntelligenceReportDetailsPage;
